import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import WordCloud from "wordcloud";
import { useLocation, useNavigate } from "react-router-dom";

import { getPreviousRoute, getNextRoute, setLocalStorage } from "../util/general.util";

import { WorkshopContext } from "../contexts/workshop.context";

import {
  ScreenBottom,
  Button,
  SpeakingOrder,
  MultiSelectionPicker,
  ParticipantsReadyList,
  ScenarioBox
} from "../components/workshop";

import { defaultRoles } from "../util/workshop.util";
import { capitalizeFirstLetter } from "../util/translations.util";
import { WordCloudRenderer } from "../components/insights";

const colors = ["#3E3E3E", "#666666", "#E59536", "#BE6A03", "#EEB52B", "#F28907"];

const predefinedDemoStrengths = {
  en: [
    "Empathetic",
    "Precise",
    "Compassionate",
    "Analytical",
    "Practical",
    "Determined",
    "Empathetic",
    "Persevering",
    "Determined",
    "Independent",
    "Analytical",
    "Determined",
    "Empathetic",
    "Creative",
    "Imaginative",
    "Empathetic",
    "Compassionate",
    "Realistic",
    "Analytical",
    "Logical",
    "Pragmatic"
  ],
  fr: [
    "Empathique",
    "Précis(e)",
    "Compatissant(e)",
    "Analytique",
    "Pratique",
    "Déterminé(e)",
    "Empathique",
    "Persévérant(e)",
    "Déterminé(e)",
    "Indépendant(e)",
    "Analytique",
    "Déterminé(e)",
    "Empathique",
    "Créatif(ive)",
    "Imaginatif(ive)",
    "Empathique",
    "Compatissant(e)",
    "Réaliste",
    "Analytique",
    "Logique",
    "Pragmatique"
  ],
  de: [
    "Empathisch",
    "Präzise",
    "Mitfühlend",
    "Analytisch",
    "Praktisch",
    "Zielstrebig",
    "Empathisch",
    "Ausdauernd",
    "Zielstrebig",
    "Unabhängig",
    "Analytisch",
    "Zielstrebig",
    "Empathisch",
    "Kreativ",
    "Fantasievoll",
    "Empathisch",
    "Mitfühlend",
    "Realistisch",
    "Analytisch",
    "Logisch",
    "Pragmatisch"
  ]
};

const getLocalizedDemoStrengths = (language) => {
  return predefinedDemoStrengths[language] || predefinedDemoStrengths.en;
};

const demoStrengthsSubmissions = {
  yavan: ["Empathetic", "Precise", "Compassionate"],
  fern: ["Empathetic", "Creative", "Imaginative"],
  fabio: ["Analytical", "Practical", "Determined"],
  tristan: ["Independent", "Analytical", "Determined"],
  patrick: ["Empathetic", "Persevering", "Determined"],
  regina: ["Empathetic", "Compassionate", "Realistic"],
  lisa: ["Analytical", "Logical", "Pragmatic"]
};

const Task6 = ({ summary = false, demoWorkshop }) => {
  const currentRoute = useLocation().pathname.split("/").pop();
  const navigate = useNavigate();
  const [taskOrderUsers, setTaskOrderUsers] = useState([]);
  const [readyUsers, setReadyUsers] = useState([]);
  const [strengthsList, setStrengthsList] = useState([]);
  const [userSelections, setUserSelections] = useState([]);

  const {
    socket,
    user: { id, role } = {},
    workshopDetails: { platform } = {},
    workshopUsers
  } = useContext(WorkshopContext);

  const { t, i18n } = useTranslation();

  const Strengths = t("resources.strengths", { returnObjects: true });

  const getRandomColor = () => {
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const renderWordCloud = (strengths) => {
    try {
      let increaseBy = 1;
      const wordsList = [];
      const originalWordsList = [];

      if (taskOrderUsers.length >= 2 && taskOrderUsers.length <= 5) increaseBy = 6;

      if (taskOrderUsers.length >= 6 && taskOrderUsers.length <= 12) increaseBy = 4;

      if (taskOrderUsers.length >= 13 && taskOrderUsers.length <= 18) increaseBy = 2.5;

      if (demoWorkshop) increaseBy = 1;

      Object.keys(strengths).forEach((item) => {
        const tempStrength = demoWorkshop ? strengths[item] : strengths[item] + 1;
        wordsList.push([capitalizeFirstLetter(item), tempStrength * increaseBy]);
        originalWordsList.push([capitalizeFirstLetter(item), strengths[item]]);
      });

      setStrengthsList(wordsList);
      if (demoWorkshop) setLocalStorage("wordsList", originalWordsList);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (demoWorkshop) {
      setTaskOrderUsers([...workshopUsers]);
    }
    socket?.emit("taskOrder", 2, (users) => {
      if (users) setTaskOrderUsers(users);
    });

    socket?.on("strengths", (strengths) => {
      if (!Object.keys(strengths)?.length) return;

      renderWordCloud(strengths);

      return () => {
        // stop the renderring
        WordCloud.stop();
      };
    });

    socket?.on("selectedStrengths", (selectedStrengths) => {
      setUserSelections(selectedStrengths);
    });

    socket?.emit("strengths", id);
  }, [id, demoWorkshop]);

  socket?.on("allUsersSelectedStrengthsReady", (readyUsers) => {
    if (readyUsers) setReadyUsers(readyUsers);
  });

  const handleBack = () => {
    const previousRoute = getPreviousRoute(`/${currentRoute}`, platform);
    if (demoWorkshop) {
      return navigate(previousRoute);
    }
    socket?.emit("navigate", previousRoute);
  };

  const handleNext = () => {
    const nextRoute = getNextRoute(`/${currentRoute}`, platform);
    if (demoWorkshop) {
      setLocalStorage("strengthsSubmissions", {
        ...demoStrengthsSubmissions,
        demo: userSelections
      });
      return navigate(nextRoute);
    }
    socket?.emit("navigate", nextRoute);
  };

  const handleDemoSelections = (selected = []) => {
    setUserSelections(selected);
    //create an object with key as the strength and value as the count of the strength
    const strengths = {};
    // Use the helper function to get the correct language strengths
    const currentDemoStrengths = getLocalizedDemoStrengths(i18n.language);
    [...selected, ...currentDemoStrengths].forEach((strength) => {
      strengths[strength] = strengths[strength] ? strengths[strength] + 1 : 1;
    });
    renderWordCloud(strengths);
  };

  useEffect(() => {
    if (demoWorkshop && taskOrderUsers.length > 0) {
      handleDemoSelections();
    }
  }, [taskOrderUsers, demoWorkshop]);

  return (
    <div className="flex flex-col gap-4 h-full w-full">
      {!summary && (
        <>
          <h1
            dangerouslySetInnerHTML={{ __html: t("mtParticipantActivityScreen.headingFive") }}
            className="text-5xl font-medium font-righteous text-gray-250"
          />

          {role === defaultRoles.participant && (
            <p
              dangerouslySetInnerHTML={{ __html: t("mtParticipantActivityScreen.paragraphFive") }}
              className="text-xl text-gray-100"
            />
          )}
          {role === defaultRoles.moderator && (
            <p
              dangerouslySetInnerHTML={{
                __html: t("mtParticipantActivityScreen.paragraphFiveMod")
              }}
              className="text-xl text-gray-100"
            />
          )}
          {role === defaultRoles.moderatorAndParticipant && (
            <p
              dangerouslySetInnerHTML={{
                __html: t("mtParticipantActivityScreen.paragraphFiveModPart")
              }}
              className="text-xl text-gray-100"
            />
          )}

          {demoWorkshop && <ScenarioBox text={t("task1.scenario")} />}

          {!demoWorkshop && <SpeakingOrder participants={taskOrderUsers} />}

          {role !== defaultRoles.moderator && (
            <MultiSelectionPicker
              socket={socket}
              moderator={role === defaultRoles.moderator}
              userId={id}
              updateId={"updateStrengths"}
              submitAllTogether={true}
              disableSelection={userSelections?.length > 0}
              dummySelections={userSelections || []}
              title={t("mtParticipantActivityScreen.dropdownOne")}
              options={Strengths?.length > 0 && Strengths?.map((s) => s?.title)}
              onChange={demoWorkshop ? handleDemoSelections : undefined}
            />
          )}
        </>
      )}

      <div className={`${!summary ? "w-full" : "w-full h-full"}`}>
        <WordCloudRenderer
          words={strengthsList}
          width={1200}
          height={1200}
          colorFunction={getRandomColor}
          weightFactorFormula={
            demoWorkshop
              ? undefined
              : (size) => {
                  const maxSize = 325;
                  const calculatedSize = Math.pow(size, 5.5);
                  return Math.min(calculatedSize, maxSize);
                }
          }
        />
      </div>

      {!summary &&
        (role === defaultRoles.moderator ||
          (role === defaultRoles.moderatorAndParticipant &&
            readyUsers.includes(id?.toString()))) && (
          <ParticipantsReadyList
            readyUsers={readyUsers}
            taskOrder={taskOrderUsers}
            idPrefix="task6-strengths"
            totalTime={180}
            showTimer={false}
            readyText={t("task.completed")}
            readyBGColor={"bg-green-300"}
          />
        )}

      {!summary && role !== defaultRoles.participant && (
        <ScreenBottom helperText={t("navigationHelperText.task6")}>
          <Button text={t("buttonBack")} onClick={handleBack} type="outline" />
          <Button text={t("buttonNext")} onClick={handleNext} />
        </ScreenBottom>
      )}
    </div>
  );
};

export default Task6;
